/**
 * Sentry Session Timing Service
 * Tracks duration from camera open to S3 upload completion
 * Uses Traces API (not deprecated transactions)
 */

import * as Sentry from "@sentry/react";
import type { Span } from "@sentry/react";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import { sentryLogException, sentryLogMessage } from "./sentryLogger";
import { getModelMetaDataTags } from "./models";

// Global reference to the active session span
let sessionSpan: Span | null = null;
let cameraOpenTimestamp: number | null = null;


export const startCameraSession = (sessionId: string | null): void => {
  sentryLogMessage({
    message: 'Camera session started',
    level: 'info',
    tags: {
      component: 'sentryTiming',
      category: 'camera_session',
      event_type: SENTRY_EVENT_TYPES.OPENED_CAMERA,
      ...getModelMetaDataTags(),
    },
    extra: {
      cameraOpenTimestamp: cameraOpenTimestamp,
    },
  });
  // End any existing session first
  if (sessionSpan) {
    sessionSpan.end();
    sessionSpan = null;
  }
  
  cameraOpenTimestamp = Date.now();
  
  sessionSpan = Sentry.startInactiveSpan({
    name: "camera-session",
    forceTransaction: true,
    op: "navigation", // 'navigation' op helps identify it as a user interaction transaction
    startTime: cameraOpenTimestamp / 1000, // Sentry uses seconds
    attributes: {
      "session_id": sessionId || "unknown",
      "camera_open_timestamp": cameraOpenTimestamp,
      "event_type": SENTRY_EVENT_TYPES.OPENED_CAMERA,
    }
  });
  
};
/**
 * Mark when camera is fully loaded and ready
 * Call this in CameraScan.tsx onLoadedData callback
 */
export const markCameraLoaded = (): void => {
  if (!sessionSpan || !cameraOpenTimestamp) return;
  
  const loadDuration = Date.now() - cameraOpenTimestamp;
  
  // Create child span for camera load timing within the transaction
  // This will appear as a child span under the camera-session transaction
  const cameraLoadSpan = Sentry.startInactiveSpan({
    name: "camera-load",
    op: "camera.init",
    startTime: cameraOpenTimestamp / 1000, // Sentry uses seconds
    attributes: {
      "camera.load_duration_ms": loadDuration,
    },
  });
  
  // Set the parent to the session span to create a proper hierarchy
  if (sessionSpan && cameraLoadSpan) {
    cameraLoadSpan.end();
  }
  
  console.log(`📊 Sentry: Camera loaded in ${loadDuration}ms`);
};

/**
 * Mark when first image is captured
 * Call this in CameraScan.tsx capture() function
 */
export const markFirstImageCapture = (): void => {
  if (!sessionSpan || !cameraOpenTimestamp) return;
  
  const captureTime = Date.now();
  const timeToCapture = captureTime - cameraOpenTimestamp;
  
  sessionSpan.setAttribute("capture.first_image_timestamp", captureTime);
  sessionSpan.setAttribute("capture.time_to_first_image_ms", timeToCapture);
  
  console.log(`📊 Sentry: First image captured at ${timeToCapture}ms`);
};

/**
 * End timing when S3 upload completes
 * Call this in imageUpload.ts after successful upload
 */
export const endCameraSession = (imageCount: number): void => {
  if (!sessionSpan || !cameraOpenTimestamp) {
    console.warn("📊 Sentry: No active session to end");
    return;
  }
  
  const endTimestamp = Date.now();
  const totalDuration = endTimestamp - cameraOpenTimestamp;
  
  try {
    // Add final attributes to the transaction
  sessionSpan.setAttribute("upload.image_count", imageCount);
  sessionSpan.setAttribute("session.total_duration_ms", totalDuration);
    sessionSpan.setAttribute("session.status", "completed");
    sessionSpan.setAttribute("event_type", SENTRY_EVENT_TYPES.CAMERA_CLOSE);
    
    // Log camera close event
    sentryLogMessage({
      message: 'Camera closed',
      level: 'info',
      tags: {
        component: 'sentryTiming',
        category: 'camera_session',
        event_type: SENTRY_EVENT_TYPES.CAMERA_CLOSE,
        ...getModelMetaDataTags(),
      },
      extra: {
        imageCount,
        totalDurationMs: totalDuration,
        timestamp: new Date().toISOString(),
      },
    });
    
  sessionSpan.end();
  
  } catch (error) {
    sentryLogException({
      error,
      tags: {
        component: 'sentryTiming',
        category: 'camera_session',
        event_type: SENTRY_EVENT_TYPES.CAMERA_CLOSE,
        ...getModelMetaDataTags(),
      },
    });
  } finally {
  // Reset state
  sessionSpan = null;
  cameraOpenTimestamp = null;
  }
};


